import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';
import { API_URL } from '../config/api';
import { isCurrentlyOpen, getTodayOpeningHours } from '../utils/openingHours';
import SEO from '../components/SEO';
import './Search.css';

const Search = () => {
  const { t } = useTranslation();
  const { addToCart } = useCart();
  const { user } = useAuth();
  const { showError } = useNotification();
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const [filters, setFilters] = useState({
    name: '',
    category: 'Everything',
    pricingRange: '',
    maxDistance: '',
    lat: '',
    lng: ''
  });

  const categories = ['Everything', 'Food', 'Wine & Liquor', 'Coffee', 'Virtual Brands', 'Other'];
  const pricingRanges = ['€', '€€', '€€€', '€€€€'];

  useEffect(() => {
    searchRestaurants();
  }, [filters]);

  const searchRestaurants = async () => {
    setLoading(true);
    try {
      const params = {};
      
      if (filters.name) params.search = filters.name;
      if (filters.category !== 'Everything') params.category = filters.category;
      if (filters.pricingRange) params.pricingRange = filters.pricingRange;
      if (filters.maxDistance) params.maxDistance = filters.maxDistance;
      if (filters.lat && filters.lng) {
        params.lat = filters.lat;
        params.lng = filters.lng;
      }

      const response = await axios.get(`${API_URL}/restaurants`, { params });
      setRestaurants(response.data);
    } catch (error) {
      console.error('Error searching restaurants:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters({
      ...filters,
      [field]: value
    });
  };

  const handleUseLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setFilters({
            ...filters,
            lat: position.coords.latitude.toString(),
            lng: position.coords.longitude.toString()
          });
        },
        (error) => {
          console.error('Error getting location:', error);
          showError('Unable to get your location');
        }
      );
    }
  };

  const handleAddToCart = (restaurant, item) => {
    addToCart(restaurant._id, restaurant.name, item);
  };

  const isRestaurantOpen = (restaurant) => isCurrentlyOpen(restaurant);

  const renderStars = (rating) => {
    if (rating == null || rating <= 0) return null;
    const stars = [];
    const fullStars = Math.min(5, Math.max(0, Math.round(rating)));
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <i key={i} className={i <= fullStars ? 'fas fa-star' : 'far fa-star'}></i>
      );
    }
    return <span className="rating-stars">{stars}</span>;
  };

  return (
    <div className="search-container">
      <SEO title={t('search.title') || 'Hae ravintoloita'} description="Hae ravintoloita nimen, kategorian tai hinnan mukaan. Tilaa ruoka kotiin." path="/search" />
      <div className="container">
        <h1>{t('search.title') || 'Search Restaurants'}</h1>

        <div className="search-filters">
          <div className="filter-group">
            <label>{t('search.restaurantName') || 'Restaurant Name'}</label>
            <input
              type="text"
              placeholder={t('search.namePlaceholder') || 'Search by name...'}
              value={filters.name}
              onChange={(e) => handleFilterChange('name', e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label>{t('search.category') || 'Category'}</label>
            <select
              value={filters.category}
              onChange={(e) => handleFilterChange('category', e.target.value)}
            >
              {categories.map(cat => (
                <option key={cat} value={cat}>
                  {t(`home.categories.${cat === 'Wine & Liquor' ? 'wineLiquor' : cat === 'Virtual Brands' ? 'virtualBrands' : cat.toLowerCase()}`)}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>{t('search.pricingRange') || 'Pricing Range'}</label>
            <select
              value={filters.pricingRange}
              onChange={(e) => handleFilterChange('pricingRange', e.target.value)}
            >
              <option value="">{t('search.allPrices') || 'All Prices'}</option>
              {pricingRanges.map(range => (
                <option key={range} value={range}>{range}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>{t('search.maxDistance') || 'Max Distance (km)'}</label>
            <input
              type="number"
              placeholder={t('search.distancePlaceholder') || 'Enter max distance'}
              value={filters.maxDistance}
              onChange={(e) => handleFilterChange('maxDistance', e.target.value)}
              min="0"
              step="0.1"
            />
          </div>

          <div className="filter-group">
            <label>{t('search.location') || 'Location'}</label>
            <button onClick={handleUseLocation} className="btn btn-primary">
              {t('search.useLocation') || 'Use Current Location'}
            </button>
          </div>
        </div>

        {loading ? (
          <div>Loading...</div>
        ) : restaurants.length === 0 ? (
          <div className="no-results">{t('search.noResults') || 'No restaurants found'}</div>
        ) : (
          <div className="search-results">
            <h2>{restaurants.length} {t('search.resultsFound') || 'results found'}</h2>
            <div className="restaurants-grid">
              {restaurants.map((restaurant) => (
                <div key={restaurant._id} className="restaurant-card">
                  <div className="restaurant-header">
                    {restaurant.image && (
                      <div className="restaurant-image-container">
                        <img src={restaurant.image} alt={restaurant.name} className="restaurant-image" loading="lazy" decoding="async" />
                        {restaurant.openingHours && !isRestaurantOpen(restaurant) && (
                          <div className="restaurant-status-overlay">
                            {t('restaurant.closed')}
                          </div>
                        )}
                        {restaurant.distance !== undefined &&
                         restaurant.distance !== null &&
                         !isNaN(restaurant.distance) && (() => {
                          const maxDist = restaurant.maxDeliveryDistance ?? 20;
                          if (restaurant.distance > maxDist) {
                            return (
                              <div className="restaurant-delivery-overlay">
                                <span>{t('restaurant.outsideDeliveryArea')}</span>
                              </div>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    )}
                  </div>
                  <div className="restaurant-info">
                    <div className="restaurant-title-row">
                      <h3>{restaurant.name}</h3>
                      {(restaurant.settings?.displayStarRatings !== false && restaurant.rating != null && restaurant.rating > 0) ? renderStars(restaurant.rating) : null}
                    </div>
                    <p className="restaurant-description">{restaurant.description}</p>
                    <div className="pricing-range">{restaurant.pricingRange || '€€'}</div>
                    <div className="restaurant-details">
                      <span>{t('restaurant.deliveryTime')}: {restaurant.deliveryTime || '30-45 min'}</span>
                      {(() => {
                        const today = getTodayOpeningHours(restaurant);
                        if (today) return <span>{today.openingTime} – {today.closingTime}</span>;
                        if (restaurant?.openingHours) return <span>{t('restaurant.closedToday') || 'Closed today'}</span>;
                        return null;
                      })()}
                      {(restaurant.distance != null && !isNaN(Number(restaurant.distance)) && Number(restaurant.distance) > 0) && (
                        <span>{Number(restaurant.distance).toFixed(1)} {t('restaurant.distance') || 'km'}</span>
                      )}
                    </div>
                    {restaurant.deliveryMethods && (
                      <div className="delivery-methods">
                        {restaurant.deliveryMethods.delivery && (
                          <span className="delivery-badge">{t('restaurant.delivery')}</span>
                        )}
                        {restaurant.deliveryMethods.pickup && (
                          <span className="delivery-badge">{t('restaurant.pickup')}</span>
                        )}
                        {restaurant.deliveryMethods.eatIn && (
                          <span className="delivery-badge">{t('restaurant.eatIn')}</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Search;
